import React, { useState, useRef, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ContactForm } from './ContactForm'
import { PreSessionScreen } from './PreSessionScreen'
import { LeaveDetailsPopup } from './LeaveDetailsPopup'
import { UploadingOverlay } from './UploadingOverlay'
import { Backdrop } from './Backdrop'
import { CalendlyPanel } from './CalendlyPanel'
import { VoiceBotHeader } from './VoiceBotHeader'
import { SessionControls } from './SessionControls'
import { SpeakingIndicator } from './SpeakingIndicator'
import { ErrorBanner } from './ErrorBanner'
import { Transcript } from './Transcript'
import { BookingStatusBanner } from './BookingStatusBanner'
import { extractCustomerNameFromTranscript } from '../utils/extract-customer-name'
import { SchedulingContactPopup } from './SchedulingContactPopup'
import { trackFacebookLead } from '../utils/facebook-pixel'
import { LanguageCode, LANGUAGE_LOCK_INSTRUCTIONS, LANGUAGE_GREETINGS, REGULATORY_STATEMENTS, DEFAULT_LANGUAGE, LANGUAGE_NAMES, DAY_NAMES, TIME_OF_DAY_LABELS } from '../config/language'
import { HEBREW_VOICE_AUDIO_CONTROL_GUIDANCE } from '../config/voice-audio-guidance'
import { typingSound } from '../utils/typing-sound'
import { TIMING } from '../constants/voice-bot-timing'
import { runKnowledgeBaseSearch } from '../assistant-tools/run-knowledge-base-search'
import { runScheduleCallback } from '../assistant-tools/run-schedule-callback'
import { runGetAvailableSlots, runCheckSpecificSlot } from '../assistant-tools/run-calendly-slots'
import { runBookMeeting } from '../assistant-tools/run-book-meeting'
import { postResidentVerifyId } from '../api/voice-bot-client'
import { runRealtimeEvent } from '../assistant-tools/run-realtime-event'
import { closeRealtimeConnection } from '../voice-session/close-realtime-connection'
import { clearVoiceSessionTimers } from '../voice-session/clear-voice-session-timers'
import { finalizeSessionRecording } from '../voice-session/finalize-session-recording'
import { sendSessionTranscript } from '../voice-session/send-session-transcript'
import { negotiateRealtimeSdp } from '../voice-session/negotiate-realtime-sdp'
import { startMixedRecording } from '../voice-session/start-mixed-recording'
import { sendInitialGreeting } from '../voice-session/send-initial-greeting'
import { flushChunkBuffer as flushChunkBufferHelper } from '../voice-session/flush-chunk-buffer'
import { startVoiceSession } from '../voice-session/start-voice-session'
import { startLiveSpeechTranscript } from '../voice-session/live-speech-transcript'
import { useIsMobile } from '../hooks/useIsMobile'
import { useDebugOverlayEvents } from '../hooks/useDebugOverlayEvents'
import { useUnloadBeacon } from '../hooks/useUnloadBeacon'
import { useCalendlyMessageListener } from '../hooks/useCalendlyMessageListener'
import { useStateAndRef } from '../hooks/useStateAndRef'
import { useTranscriptScroll } from '../hooks/useTranscriptScroll'
import { useCalendlyOverlaySync } from '../hooks/useCalendlyOverlaySync'
import type { CrossChannelMemory } from '../hooks/useCrossChannelMemory'
import type {
  Message,
  VoiceOption,
  LanguageOption,
  VoiceSessionData,
  CalendlySlot,
  ScheduledMeeting,
  ConversationOutcome,
  ContactInfo,
  VoiceHardeningConfig,
} from '../types'

interface VoiceBotPanelProps {
  isOpen: boolean
  onClose: () => void
  /** Optional: when provided, the header renders a chat-icon switch
   *  button next to Close. Multi-channel bots wire this via
   *  AssistantShell. Voice-only bots leave it undefined and the
   *  button is not rendered. */
  onSwitchToChat?: () => void
  /** Localized label for the switch-to-chat button (tooltip + aria). */
  switchToChatLabel?: string
  /**
   * Shared cross-channel conversation memory. When non-empty at session
   * start, prior turns are injected into the Realtime session via
   * `conversation.item.create` events BEFORE the model's first response
   * so the bot continues exactly where the chat (or prior voice) session
   * left off — no greeting replay. Every voice transcript line is also
   * pushed back into this memory so a later switch to chat retains the
   * full history. Optional — when omitted, voice behaves as a fresh
   * session with the canonical opening greeting.
   */
  crossChannelMemory?: CrossChannelMemory
  config: {
    publicKey?: string // Customer public key for multi-tenant authentication
    botName: string
    companyName: string
    greeting: string
    voice: VoiceOption
    language: LanguageOption
    temperature: number
    primaryColor: string
    panelWidth: number
    systemPrompt: string
    callerAddressMode?: 'auto' | 'plural' | 'adaptive_plural' | null
    calendlyOwnerName: string
    propertyCode?: string // Property code for context-aware greeting (e.g., "HAD4", "KIR1")
    forceVoiceOverride?: boolean // Demo-only explicit voice override
    recordingsFolderId?: string // Google Drive folder ID for recording shortcuts
    transcriptsFolderId?: string // Google Drive folder ID for transcript shortcuts
    poweredByText?: string // Customizable "Powered by" branding text
    contactFormTiming?: 'none' | 'before_conversation' | 'during_conversation' | 'before_scheduling' // When to show contact form
    voiceHardening?: VoiceHardeningConfig
    blockInterruptionDefault?: boolean // Default interruption blocking state
    voiceRecordingDisclosureEnabled?: boolean // Web-voice only: false = skip AI/recording disclosure, start with greeting (matches chat)
    liveTranscriptEngine?: 'azure_speech' | null // Phase B: stream user transcript via Azure Speech SDK
    realtimeTranscribeModel?: string | null // Realtime input-transcription model override (voice-only)
  }
}

export const VoiceBotPanel: React.FC<VoiceBotPanelProps> = ({
  isOpen,
  onClose,
  onSwitchToChat,
  switchToChatLabel,
  crossChannelMemory,
  config: propConfig,
}) => {
  // Active config: starts with props, may be overridden by server widgetConfig
  const [config, setConfig] = useState(propConfig)
  const console = !import.meta.env.PROD
    ? globalThis.console
    : {
        ...globalThis.console,
        log: (..._args: unknown[]) => undefined,
        info: (..._args: unknown[]) => undefined,
        debug: (..._args: unknown[]) => undefined,
        warn: (..._args: unknown[]) => undefined,
        error: globalThis.console.error.bind(globalThis.console),
      }
  
  console.log('🔍 VoiceBotPanel received config:', config) // DEBUG
  console.log('🔍 VoiceBotPanel propertyCode:', config.propertyCode) // DEBUG

  // Update config when props change (but preserve server overrides if session is active)
  useEffect(() => {
    if (!isSessionActive) {
      setConfig(propConfig)
    }
  }, [propConfig])

  // Keep configRef in sync with config state (for webhook payload)
  useEffect(() => {
    configRef.current = config
  }, [config])

  // Session state
  const [isSessionActive, setIsSessionActive] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)
  // Phase B: live streaming user transcript partial (azure_speech engine only).
  const [userPartial, setUserPartial] = useState('')
  // Wave 6e1: isMuted state + ref consolidated via useStateAndRef.
  // The setter writes isMutedRef.current synchronously, so any callsite
  // that previously did `setIsMuted(x); isMutedRef.current = x` only
  // needs the setter call now.
  const [isMuted, setIsMuted, isMutedRef] = useStateAndRef(false)
  const [error, setError] = useState<string | null>(null)
  const [blockInterruption, setBlockInterruption, blockInterruptionRef] = useStateAndRef(
    config.blockInterruptionDefault ?? false
  ) // Default from customer config
  const [showInterruptTooltip, setShowInterruptTooltip] = useState(false)

  // Transcript
  const [transcript, setTranscript] = useState<Message[]>([])
  const transcriptRef = useRef<HTMLDivElement>(null)
  const transcriptMessagesRef = useRef<Message[]>([])
  const lastTravelContextHintsRef = useRef<string[]>([])
  const currentBotResponse = useRef<string>('')

  // Meeting state
  const [scheduledMeeting, setScheduledMeeting] = useState<ScheduledMeeting | null>(null)
  const [showCalendlyEmbed, setShowCalendlyEmbed, showCalendlyEmbedRef] = useStateAndRef(false)
  const [bookingConfirmed, setBookingConfirmed, bookingConfirmedRef] = useStateAndRef(false)
  // Wave 6e3: pendingCallEnd / pendingReminder are ref-backed. The useCallback
  // that owns handleDataChannelMessage captures plain state values STALE (they
  // are not in its dep array), so the goodbye → close branch in the dispatcher
  // used to read pendingCallEnd === false forever and never end the session.
  // The ref always reflects the current value.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_pendingCallEnd, setPendingCallEnd, pendingCallEndRef] = useStateAndRef(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_pendingReminder, setPendingReminder, pendingReminderRef] = useStateAndRef(false)
  // Wave 6e2: pendingEndAfterSpeaking state+ref consolidated. Setter
  // writes pendingEndAfterSpeakingRef.current synchronously, so the
  // previously-paired imperative ref writes at every call site are
  // gone (and a stale handlePanelClose setter-only call now also
  // resets the ref correctly).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_pendingEndAfterSpeaking, setPendingEndAfterSpeaking, pendingEndAfterSpeakingRef] =
    useStateAndRef(false) // Wait for TTS to finish before ending
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_availableSlots, setAvailableSlots] = useState<CalendlySlot[]>([])
  const [propertyName, setPropertyName] = useState<string | null>(null) // Property name for context-aware greeting

  // Mobile detection (viewport + UA), updates on resize
  const isMobile = useIsMobile()
  const [showCalendlyOverlay, setShowCalendlyOverlay] = useState(false)

  // Contact form state
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null)
  const [showContactForm, setShowContactForm] = useState(false)
  const [showPreSessionScreen, setShowPreSessionScreen] = useState(false)
  const [showLeaveDetailsPopup, setShowLeaveDetailsPopup] = useState(false)
  const [showSchedulingPopup, setShowSchedulingPopup] = useState(false) // Minimal email+phone popup before Calendly
  const pendingCustomerNameRef = useRef<string | null>(null) // Customer name bot collected, passed to scheduling popup
  // Wave 0b follow-up: forward-ref to handleFunctionCall so the
  // useDebugOverlayEvents hook (declared earlier) can dispatch test
  // tool calls. Assigned below the handleFunctionCall definition.
  const handleFunctionCallRef = useRef<
    | ((callId: string, name: string, args: Record<string, unknown>) => Promise<void>)
    | null
  >(null)

  // A/B testing tracking
  const contactInfoSourceRef = useRef<'pre_session_form' | 'pre_calendly_form' | 'during_conversation_popup' | 'post_session_popup' | null>(null)
  const reachedSchedulingRef = useRef(false) // Track if bot called book_meeting
  const deferredContactResolveRef = useRef<((info: ContactInfo | null) => void) | null>(null) // Promise resolver for deferred form
  const leaveDetailsResolveRef = useRef<(() => void) | null>(null) // Promise resolver for post-session popup
  const sessionCompletedRef = useRef(false) // Tracks if a session ran and ended — prevents re-showing pre-session screens

  const resetDeferredLeadState = () => {
    setContactInfo(null)
    contactInfoRef.current = null
    contactInfoSourceRef.current = null
    reachedSchedulingRef.current = false
    pendingCustomerNameRef.current = null
    deferredContactResolveRef.current = null
    leaveDetailsResolveRef.current = null
    setShowSchedulingPopup(false)
    setShowLeaveDetailsPopup(false)
  }

  const handlePanelClose = () => {
    resetDeferredLeadState()
    sessionCompletedRef.current = false
    lastTravelContextHintsRef.current = []
    setShowContactForm(false)
    setShowPreSessionScreen(false)
    setScheduledMeeting(null)
    scheduledMeetingRef.current = null
    setShowCalendlyEmbed(false)
    setBookingConfirmed(false)
    setPendingCallEnd(false)
    setPendingReminder(false)
    setPendingEndAfterSpeaking(false)
    setTranscript([])
    currentBotResponse.current = ''
    onClose()
  }

  // Upload state (blocking UI during audio upload)
  const [isUploading, setIsUploading] = useState(false)

  // Track if there's an active response in progress (to prevent concurrent responses)
  const activeResponseRef = useRef(false)
  const pendingResponseRef = useRef(false) // Queue response.create for when current response finishes
  const responseHadAudioRef = useRef(false) // Track if current response generated audio (for greeting retry)
  const lastResponseTriggerTimeRef = useRef(0) // Debounce rapid-fire response triggers
  const responseStallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null) // Recover if response.done never arrives
  const tokensUsedRef = useRef(0) // Accumulate total_tokens from response.done events
  const lastUserMessageRef = useRef('') // Latest transcribed user message
  const currentUserTranscriptRef = useRef('')
  const currentUserTranscriptItemRef = useRef<string | null>(null)
  const committedUserTranscriptItemsRef = useRef<Set<string>>(new Set())
  const userTranscriptFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const insuranceRecoveryForMessageRef = useRef('') // Prevent duplicate insurance no-info recovery per user msg

  // Initial greeting state - track pre-greeting audio to ignore
  const initialGreetingCompleteRef = useRef(false)
  const preGreetingAudioCommitsRef = useRef(0) // Count of audio commits before greeting finished
  const transcriptionsToSkipRef = useRef(0) // Number of transcriptions to skip
  const pendingGreetingUnmuteRef = useRef(false) // Wait for audio to complete before unmuting
  const greetingRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null) // Retry greeting if no audio
  // Phase B: Azure Speech streaming live-transcript (voice-only, flag-gated).
  const liveSpeechHandleRef = useRef<{ stop: () => void } | null>(null)
  const isSpeakingRef = useRef(false) // Mirrors isSpeaking for the live-transcript gate

  // Refs to track booking state in callbacks (avoid stale closures)
  const contactInfoRef = useRef<ContactInfo | null>(null) // Persist contactInfo across renders
  const selectedLanguageRef = useRef<LanguageCode>(DEFAULT_LANGUAGE) // Persist selected language for Whisper
  const scheduledMeetingRef = useRef<ScheduledMeeting | null>(null)
  // showCalendlyEmbedRef is now owned by useStateAndRef above (Wave 6e3).
  // bookingConfirmedRef is now owned by useStateAndRef above (Wave 6e5).
  const goodbyeSentRef = useRef(false) // Prevent duplicate goodbye messages
  // blockInterruptionRef is now owned by useStateAndRef above (Wave 6e4).
  const pendingLinksRef = useRef<Array<{ label: string; url: string }> | null>(null) // Affiliate links waiting to attach to next bot message
  // pendingEndAfterSpeakingRef is now owned by useStateAndRef above (Wave 6e2).
  const configRef = useRef(config) // Track latest config for webhook payload (avoid stale closures)
  const calendlyEventFetchRef = useRef<Promise<void> | null>(null) // Await in endSession to ensure actual booking time is captured

  // Residents (Dona) — VOICE identity-verification gate state. Voice-only and
  // industry-gated (sessionDataRef.current?.industry === 'residents'). Mirrors
  // the chat verify→search flow but adds an explicit caller "read-back"
  // confirmation step: the model must repeat the ת.ז + apartment aloud and the
  // caller must confirm (confirm_resident_identity) BEFORE any personal search.
  // These are untrusted hints — the SERVER still re-resolves the contract via
  // HMAC on every /api/knowledge-base call.
  const residentVerifiedTzRef = useRef<string | null>(null) // last ת.ז that passed verify_resident_id (digits only)
  const residentConfirmedRef = useRef<{ tz: string; apartment: string } | null>(null) // identity confirmed aloud by the caller

  // WebRTC refs
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null)
  const dataChannelRef = useRef<RTCDataChannel | null>(null)
  const audioElementRef = useRef<HTMLAudioElement | null>(null)
  const sessionDataRef = useRef<VoiceSessionData | null>(null)
  const sessionStartTime = useRef<number>(0)
  const lastActivityTime = useRef<number>(0)
  const idleTimeoutRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Audio recording refs (for Azure Blob Storage)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const recordedAudioBlobRef = useRef<Blob | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const mixedStreamRef = useRef<MediaStream | null>(null)

  // Beacon safety: track if transcript was sent, so pagehide can fire a fallback
  const transcriptSentRef = useRef(false)
  // Tracks whether post-call processing (finalization + transcript send) is still running.
  // Unlike isSessionActive (set to false immediately for UI), this stays true until transcript is sent.
  const postCallInProgressRef = useRef(false)
  const recordingDataRef = useRef<{ url: string | null; storagePath: string | null; size: number; type: string }>({ url: null, storagePath: null, size: 0, type: 'audio/webm' })

  // Progressive chunk upload refs — upload audio during the call so tab-close loses at most ~10s
  const chunkUploadActiveRef = useRef(false)
  const chunkIndexRef = useRef(0)
  const chunkBufferRef = useRef<Blob[]>([])
  const chunkUploadPromisesRef = useRef<Promise<boolean>[]>([])
  const CHUNK_FLUSH_INTERVAL = 10 // Flush every 10 ondataavailable events (= 10 seconds)

  // Wave 6 cross-channel safety net: latest-snapshot refs so the unmount
  // cleanup below can fire endSession() without putting it in the
  // effect's dep array (which would invalidate cleanup on every render).
  const endSessionRef = useRef<(() => Promise<void>) | null>(null)
  const isSessionActiveRef = useRef(false)

  // Beacon safety: if user closes tab before endSession finishes, fire a minimal payload
  useUnloadBeacon({
    isSessionActive,
    postCallInProgressRef,
    transcriptSentRef,
    sessionStartTime,
    sessionDataRef,
    transcriptMessagesRef,
    bookingConfirmedRef,
    scheduledMeetingRef,
    contactInfoRef,
    configRef,
    selectedLanguageRef,
    recordingDataRef,
    tokensUsedRef,
    contactInfoSourceRef,
    reachedSchedulingRef,
    chunkUploadActiveRef,
    chunkIndexRef,
  })

  // Keep the isSessionActive snapshot ref in lock-step with state so the
  // unmount cleanup below can read the latest value.
  useEffect(() => {
    isSessionActiveRef.current = isSessionActive
  })

  // Wave 6 — UNMOUNT CLEANUP (critical multi-channel safety net):
  // If VoiceBotPanel unmounts with a live voice session (e.g. the user
  // switched back to chat via the channel switcher, or the parent shell
  // closed the widget), we MUST tear down the WebRTC connection,
  // MediaRecorder, microphone tracks, and AudioContext. Without this,
  // the Realtime model keeps streaming responses, the mic stays open,
  // and chunked recording uploads continue invisibly — wasting tokens,
  // leaking mic permission, and confusing the user when they see voice
  // activity in the console after closing the widget from chat mode.
  //
  // We fire endSession() (NOT just closeRealtimeConnection) so the full
  // post-call flow runs: recording finalize, transcript send, usage log.
  // It's async but its synchronous prelude stops the MediaRecorder and
  // closes the mic immediately; the rest finishes in the background.
  useEffect(() => {
    return () => {
      if (
        isSessionActiveRef.current &&
        !postCallInProgressRef.current &&
        endSessionRef.current
      ) {
        // eslint-disable-next-line no-console
        console.log(
          '🛑 VoiceBotPanel unmounting with active voice session — running endSession() to tear down WebRTC + upload recording + send transcript',
        )
        endSessionRef.current().catch((err) => {
          // eslint-disable-next-line no-console
          console.warn('Unmount endSession error:', err)
        })
      }
    }
  }, [])

  useDebugOverlayEvents({
    openSchedulingPopup: (customerName) => {
      setShowContactForm(false)
      setShowPreSessionScreen(false)
      setShowLeaveDetailsPopup(false)
      pendingCustomerNameRef.current = customerName || 'לקוח לדוגמה'
      setShowSchedulingPopup(true)
    },
    openLeavePopup: () => {
      setShowContactForm(false)
      setShowPreSessionScreen(false)
      setShowSchedulingPopup(false)
      setShowLeaveDetailsPopup(true)
    },
    closePopups: () => {
      setShowSchedulingPopup(false)
      setShowLeaveDetailsPopup(false)
    },
    callTool: (name, args, callId) => {
      // Wave 0b follow-up: synthetic dispatch to handleFunctionCall via
      // ref so Playwright can drive extracted tool helpers without WebRTC.
      // The ref is assigned below after handleFunctionCall is defined.
      handleFunctionCallRef.current?.(callId, name, args).catch((err) => {
        console.warn('[debug] iland:test-call-tool failed', err)
      })
    },
  })

  // Show contact form or pre-session screen based on timing config
  // Re-runs when contactFormTiming changes (e.g., after server config loads)
  // Only routes if user hasn't already submitted contact info or started a session
  useEffect(() => {
    if (contactInfo || isSessionActive || sessionCompletedRef.current) return // User already interacted or session ended
    const timing = config.contactFormTiming || 'none'
    if (timing === 'none' || timing === 'before_scheduling' || timing === 'during_conversation') {
      // Show pre-session screen (language + tips + consent) — no email/phone collection
      setShowContactForm(false)
      setShowPreSessionScreen(true)
    } else {
      // before_conversation mode: show full contact form
      setShowPreSessionScreen(false)
      setShowContactForm(true)
    }
  }, [config.contactFormTiming])

  // Reset form when panel is opened and user hasn't submitted contact info
  useEffect(() => {
    if (isOpen && !contactInfo && !isSessionActive && !sessionCompletedRef.current) {
      const timing = config.contactFormTiming || 'none'
      if (timing === 'none' || timing === 'before_scheduling' || timing === 'during_conversation') {
        if (!showPreSessionScreen) setShowPreSessionScreen(true)
      } else {
        if (!showContactForm) setShowContactForm(true)
      }
    }
  }, [isOpen, contactInfo, isSessionActive])

  // Handle contact form submission (full form — email + phone + language, normal mode only)
  const handleContactFormSubmit = (email: string, phone: string, language: LanguageCode) => {
    // Update state AND ref (ref persists across renders)
    const info = { email, phone }
    setContactInfo(info)
    contactInfoRef.current = info
    // Track the source for A/B analytics
    if (!contactInfoSourceRef.current) {
      contactInfoSourceRef.current = 'pre_session_form'
    }
    // Store selected language for Whisper and system prompt (ref only, no re-render needed)
    selectedLanguageRef.current = language
    console.log('✅ Contact info saved:', info, 'Language:', language, 'Source:', contactInfoSourceRef.current)
    setShowContactForm(false)

    // Auto-start voice session after a short delay (normal mode)
    setTimeout(async () => {
      await startSession()
    }, 300)
  }

  // Handle pre-session screen ready (deferred mode — language only, no email/phone)
  const handlePreSessionReady = (language: LanguageCode) => {
    resetDeferredLeadState()
    sessionCompletedRef.current = false
    selectedLanguageRef.current = language
    console.log('✅ Pre-session ready. Language:', language, '(contact form deferred to pre-Calendly)')
    setShowPreSessionScreen(false)

    // Auto-start voice session (no contact info yet)
    setTimeout(async () => {
      await startSession()
    }, 300)
  }

  // Handle deferred contact form for pre-Calendly — shows minimal scheduling popup
  const waitForDeferredContactInfo = (): Promise<ContactInfo | null> => {
    return new Promise((resolve) => {
      deferredContactResolveRef.current = resolve
      setShowSchedulingPopup(true)
    })
  }

  // Auto-scroll transcript (requestAnimationFrame ensures DOM has rendered)
  // Wave 6f: transcript auto-scroll + transcriptMessagesRef sync now
  // owned by useTranscriptScroll. The two inline effects that watched
  // [transcript] are gone — same behavior, less noise here.
  useTranscriptScroll(transcript, transcriptRef, transcriptMessagesRef)

  // Wave 6g: Calendly mobile overlay open + auto-dismiss now owned by
  // useCalendlyOverlaySync. The two inline effects (showCalendlyEmbed
  // → overlay; bookingConfirmed → 2s auto-dismiss) moved into the hook,
  // and the auto-dismiss timeout is now properly cleared on cleanup.
  useCalendlyOverlaySync(
    isMobile,
    showCalendlyEmbed,
    bookingConfirmed,
    showCalendlyOverlay,
    setShowCalendlyOverlay
  )

  // Sync refs with state for callback access
  // (showCalendlyEmbedRef and bookingConfirmedRef self-sync via useStateAndRef — Waves 6e3/6e5.)
  useEffect(() => {
    scheduledMeetingRef.current = scheduledMeeting
    console.log('🔔 Reminder tracking state:', { scheduledMeeting: !!scheduledMeeting, showCalendlyEmbed, bookingConfirmed })
  }, [scheduledMeeting, showCalendlyEmbed, bookingConfirmed])

  useEffect(() => {
    const defaultBlockInterruption = config.blockInterruptionDefault ?? false
    // Wave 6e4: setBlockInterruption now writes blockInterruptionRef
    // synchronously, so the previously-paired imperative ref write is gone.
    setBlockInterruption(defaultBlockInterruption)
  }, [config.blockInterruptionDefault])

  // Wave 6e4: blockInterruption ref self-syncs via useStateAndRef.
  // The old `useEffect(() => { blockInterruptionRef.current = blockInterruption })`
  // is no longer needed.

  // Wave 6e1: isMuted ref is kept in sync by useStateAndRef. The old
  // `useEffect(() => { isMutedRef.current = isMuted }, [isMuted])`
  // was removed because the setter writes the ref synchronously now.

  // Wave 6e2: pendingEndAfterSpeaking ref is kept in sync by useStateAndRef.
  // The old sync useEffect is gone for the same reason.

  // CENTRALIZED RESPONSE TRIGGER - prevents race conditions and rapid-fire triggers
  // All response.create calls should go through this helper
  const triggerResponse = useCallback((source: string, forceQueue = false) => {
    if (!dataChannelRef.current || dataChannelRef.current.readyState !== 'open') {
      console.log(`⚠️ triggerResponse(${source}): Data channel not open`)
      return false
    }

    const now = Date.now()
    const timeSinceLastTrigger = now - lastResponseTriggerTimeRef.current

    // Debounce: Ignore triggers within debounce period (prevents timer collision)
    if (timeSinceLastTrigger < TIMING.DEBOUNCE_MS && !forceQueue) {
      console.log(`⏳ triggerResponse(${source}): Debounced (${timeSinceLastTrigger}ms since last)`)
      return false
    }

    if (!activeResponseRef.current) {
      activeResponseRef.current = true
      lastResponseTriggerTimeRef.current = now
      dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }))
      console.log(`📤 triggerResponse(${source}): Sent response.create`)
      return true
    } else {
      pendingResponseRef.current = true
      console.log(`⏳ triggerResponse(${source}): Queued (response in progress)`)
      return false
    }
  }, [])

  // Helper: Control microphone based on bot speaking state (for non-interrupt mode)
  const setMicrophoneForBotSpeaking = useCallback((botIsSpeaking: boolean) => {
    if (!peerConnectionRef.current || !blockInterruptionRef.current) return
    
    // If user manually muted, never unmute — respect their choice
    if (!botIsSpeaking && isMutedRef.current) {
      console.log('🎤 Bot done speaking, but user manually muted — keeping muted')
      return
    }
    
    // When blockInterruption is ON and bot is speaking, mute the mic to prevent interruption
    // When bot stops speaking, unmute so user can talk
    peerConnectionRef.current.getSenders().forEach(sender => {
      if (sender.track?.kind === 'audio') {
        sender.track.enabled = !botIsSpeaking
        console.log(`🎤 Mic ${botIsSpeaking ? 'muted (bot speaking, no-interrupt mode)' : 'unmuted (bot done, your turn)'}`)
      }
    })
  }, [])

  // Idle timeout - close session after inactivity (pauses during Calendly booking)
  useEffect(() => {
    if (isSessionActive) {
      // Initialize last activity time
      lastActivityTime.current = Date.now()
      // Check for idle periodically
      idleTimeoutRef.current = setInterval(() => {
        // PAUSE idle timeout while Calendly is open (user is filling form)
        if (showCalendlyEmbed) {
          lastActivityTime.current = Date.now() // Reset timer while Calendly is open
          return
        }
        
        const idleSeconds = (Date.now() - lastActivityTime.current) / 1000
        if (idleSeconds >= TIMING.IDLE_TIMEOUT_SECONDS) {
          console.log(`⏰ Session idle for ${TIMING.IDLE_TIMEOUT_SECONDS} seconds - auto-closing...`)
          endSession()
        }
      }, TIMING.IDLE_CHECK_INTERVAL_MS)
      
      return () => {
        if (idleTimeoutRef.current) {
          clearInterval(idleTimeoutRef.current)
          idleTimeoutRef.current = null
        }
      }
    }
  }, [isSessionActive, showCalendlyEmbed])


  // Build system instructions
  // configOverride: Optional config to use instead of state (for avoiding stale closures)
  const buildSystemInstructions = useCallback((contextPropertyName?: string | null, configOverride?: typeof config): string => {
    // Use override config if provided, otherwise use state
    const activeConfig = configOverride || config
    // Use provided property name or fall back to state
    const activePropertyName = contextPropertyName !== undefined ? contextPropertyName : propertyName
    // Get current date/time in customer's timezone
    const now = new Date()
    const englishDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    const currentDay = englishDays[now.getDay()]
    const currentDate = `${now.getDate()}/${now.getMonth() + 1}/${now.getFullYear()}`
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`
    const timeContext = `Current date and time: ${currentDay}, ${currentDate}, ${currentTime} (customer's local time)`

    // Get the hard language lock instruction for the selected language
    const languageLockInstruction = LANGUAGE_LOCK_INSTRUCTIONS[selectedLanguageRef.current]
    const selectedLang = selectedLanguageRef.current

    // Use language-appropriate greeting and regulatory statement
    // For non-Hebrew languages, use the pre-defined greetings from config/language.ts
    let contextualGreeting: string
    let regulatoryStatement: string

    if (selectedLang === 'he') {
      // Hebrew: use config greeting (customer-specific)
      contextualGreeting = activeConfig.greeting
      if (activePropertyName) {
        // Insert property mention after the initial greeting, before asking for name
        const parts = activeConfig.greeting.split('אז עם מי יש לי את הכבוד?')
        if (parts.length === 2) {
          const propertyInterestPhrase = ['plural', 'adaptive_plural'].includes(activeConfig.callerAddressMode || '')
            ? `אני רואה שאתם מתעניינים ב${activePropertyName}!`
            : `אני רואה שאתה מתעניין ב${activePropertyName}!`
          contextualGreeting = `${parts[0].trim()} ${propertyInterestPhrase} אז עם מי יש לי את הכבוד?${parts[1] || ''}`
        }
      }
      regulatoryStatement = REGULATORY_STATEMENTS['he']
    } else {
      // Non-Hebrew: use language-specific greetings from config
      // Replace hardcoded 'M' with the actual bot name from config
      const baseGreeting = LANGUAGE_GREETINGS[selectedLang] || LANGUAGE_GREETINGS['en']
      contextualGreeting = baseGreeting.replace(/\{botName\}/g, activeConfig.botName || 'Assistant')
      regulatoryStatement = REGULATORY_STATEMENTS[selectedLang] || REGULATORY_STATEMENTS['en']
    }

    const fullGreeting = activeConfig.voiceRecordingDisclosureEnabled === false
      ? contextualGreeting
      : `${regulatoryStatement} ${contextualGreeting}`

    // Build final language reminder (only for non-Hebrew to counteract Hebrew examples in prompt)
    const languageReminder = selectedLang !== 'he' 
      ? `\n\n═══════════════════════════════════════════════════
🔒 FINAL LANGUAGE REMINDER (CRITICAL)
═══════════════════════════════════════════════════
Despite any Hebrew examples or patterns in this system prompt, you MUST respond ONLY in ${LANGUAGE_NAMES[selectedLang] || 'English'}.
The Hebrew text above is for reference ONLY - your responses must be entirely in the user's language.
If you catch yourself about to respond in Hebrew - STOP and rephrase in ${LANGUAGE_NAMES[selectedLang] || 'English'}.`
      : ''
    const callerAddressFinalReminder = selectedLang === 'he' && activeConfig.callerAddressMode === 'plural'
      ? `\n\n🔒 FINAL CALLER ADDRESS LOCK - HIGHEST PRIORITY:
בכל פנייה בעברית ללקוח השתמש/י בלשון רבים זכר בלבד. אם הוראה, דוגמה, ברכה, זרימת שיחה או טקסט מערוץ מסוים משתמשים בלשון יחיד או מבקשים לזהות מגדר - יש להתעלם מהם ולהמיר את הפנייה ללשון רבים זכר. אין לזהות או להכריז על מגדר הלקוח ואין לעבור ללשון יחיד במהלך השיחה.`
      : selectedLang === 'he' && activeConfig.callerAddressMode === 'adaptive_plural'
        ? `\n\n🔒 FINAL ADAPTIVE CALLER ADDRESS LOCK - HIGHEST PRIORITY:
ברירת המחדל בעברית היא פנייה בלשון רבים זכר. אין לעבור ליחיד בגלל קול, שם, הצהרת זהות או דקדוק רגיל. אם הלקוח מבקש במפורש לפנות אליו בלשון זכר או נקבה, אשר/י פעם אחת בקצרה ועבור/י מיד ובעקביות ללשון היחיד שביקש. בקשה מפורשת מאוחרת יותר מתקנת את הבחירה. מגדר הסוכן/ת עצמו/ה נשאר לפי BOT_GRAMMATICAL_GENDER.`
      : ''
    const voiceAudioControlGuidance = selectedLang === 'he'
      ? HEBREW_VOICE_AUDIO_CONTROL_GUIDANCE
      : ''

    // Build property context block (runtime context - only when user arrived from a specific property page)
    const propertyContextBlock = activePropertyName
      ? `\n\n🏡 PROPERTY CONTEXT (CRITICAL - DO NOT IGNORE):
The customer arrived from the page of "${activePropertyName}" and is specifically interested in this property.
- Do NOT ask "in which area" / "באיזה אזור" or "in which city" / "באיזה עיר" — the customer already chose a property.
- Start the conversation assuming the customer wants to learn about "${activePropertyName}".
- When using search_knowledge_base, search for "${activePropertyName}" first.
- Skip any conversation flow steps that ask about location, area, or city preference.
- If the customer asks about other properties or areas, then you may expand the conversation.\n`
      : ''

    // Inject language lock at TOP AND END of system prompt (sandwich pattern for maximum compliance)
    return `${languageLockInstruction}

${activeConfig.systemPrompt}
${propertyContextBlock}
${timeContext}

OPENING GREETING (MUST SAY EXACTLY - DO NOT MODIFY OR PARAPHRASE):
When you first start the conversation, say EXACTLY this text word-for-word, including punctuation:
"${fullGreeting}"

⚠️ CRITICAL: Do NOT change or rephrase the greeting. Say it EXACTLY as written above.

Remember: Keep responses concise (2-4 sentences). No emojis or special characters.${languageReminder}${callerAddressFinalReminder}${voiceAudioControlGuidance}`
  }, [config, propertyName])

  // Add message to transcript
  const addMessage = useCallback((role: 'user' | 'assistant', content: string, links?: Array<{ label: string; url: string }>) => {
    if (!content.trim()) return
    setTranscript(prev => [...prev, { role, content: content.trim(), timestamp: new Date(), links }])
    // Cross-channel memory write: persist every voice turn so a future
    // switch to chat can seed itself with the full prior context. The
    // shared memory handles dedup + cap + char trimming internally.
    crossChannelMemory?.append({ role, content, source: 'voice' })
  }, [crossChannelMemory])

  // Helper to format time in Hebrew
  const formatTime = (dateStr: string, lang: LanguageCode): string => {
    const date = new Date(dateStr)
    const days = DAY_NAMES[lang] || DAY_NAMES['he']
    const timeLabels = TIME_OF_DAY_LABELS[lang] || TIME_OF_DAY_LABELS['he']
    const dayName = days[date.getDay()]
    const dayNum = date.getDate()
    const month = date.getMonth() + 1
    const hour = date.getHours()
    const minutes = date.getMinutes()
    const minStr = minutes > 0 ? `:${minutes.toString().padStart(2, '0')}` : ''

    // Determine time-of-day label
    let periodLabel = ''
    if (hour < 12) {
      periodLabel = timeLabels.morning
    } else if (hour === 12) {
      periodLabel = timeLabels.noon
    } else if (hour < 17) {
      periodLabel = timeLabels.afternoon
    } else {
      periodLabel = timeLabels.evening
    }

    // Hebrew keeps the word-based format for natural speech
    if (lang === 'he') {
      let timeStr = ''
      if (hour < 12) {
        timeStr = `בשעה ${hour === 0 ? 'שתיים עשרה' : hour} ${minutes === 30 ? 'וחצי ' : ''}בבוקר`
      } else if (hour === 12) {
        timeStr = `בשעה שתיים עשרה ${minutes === 30 ? 'וחצי ' : ''}בצהריים`
      } else {
        const pmHour = hour - 12
        const pmHourWords: Record<number, string> = {
          1: 'אחת', 2: 'שתיים', 3: 'שלוש', 4: 'ארבע', 5: 'חמש', 6: 'שש', 7: 'שבע', 8: 'שמונה', 9: 'תשע', 10: 'עשר', 11: 'אחת עשרה'
        }
        timeStr = `בשעה ${pmHourWords[pmHour] || pmHour} ${minutes === 30 ? 'וחצי ' : ''}${hour < 17 ? 'אחר הצהריים' : 'בערב'}`
      }
      return `${dayName}, ${dayNum}/${month} ${timeStr}`
    }

    // For all other languages: use 12-hour or 24-hour numeric format
    const displayHour = lang === 'en' ? (hour % 12 || 12) : hour
    return `${dayName}, ${dayNum}/${month} ${displayHour}${minStr} ${periodLabel}`
  }

  // Store the selected Calendly time when user picks a slot (before booking confirmed)
  const calendlySelectedTimeRef = useRef<string | null>(null)
  
  // Listen for Calendly booking confirmation via postMessage
  useCalendlyMessageListener({
    calendlySelectedTimeRef,
    scheduledMeetingRef,
    sessionDataRef,
    calendlyEventFetchRef,
    selectedLanguageRef,
    dataChannelRef,
    goodbyeSentRef,
    bookingConfirmedRef,
    setScheduledMeeting,
    setBookingConfirmed,
    setShowCalendlyEmbed,
    formatTime,
    triggerResponse,
    // endSession is declared further down; wrap in a thunk so the
    // reference is deferred until the message listener actually fires.
    endSession: () => endSession(),
  })

  // Handle data channel messages
  const handleDataChannelMessage = useCallback((event: MessageEvent) => {
    // CRITICAL: Wrap entire handler in try-catch to prevent crashes from malformed data
    let message: any
    try {
      message = JSON.parse(event.data)
    } catch (parseError) {
      console.error('❌ Failed to parse data channel message:', parseError)
      console.log('   Raw data:', typeof event.data === 'string' ? event.data.substring(0, 200) : event.data)
      return // Skip this message but don't crash the handler
    }
    
    console.log('📩 Event:', message.type)

    try {
      runRealtimeEvent(message, {
        transcriptionsToSkipRef,
        dataChannelRef,
        initialGreetingCompleteRef,
        lastUserMessageRef,
        lastActivityTime,
        selectedLanguageRef,
        scheduledMeetingRef,
        showCalendlyEmbedRef,
        bookingConfirmedRef,
        goodbyeSentRef,
        currentBotResponse,
        responseHadAudioRef,
        pendingLinksRef,
        sessionDataRef,
        insuranceRecoveryForMessageRef,
        greetingRetryTimeoutRef,
        pendingEndAfterSpeakingRef,
        peerConnectionRef,
        pendingGreetingUnmuteRef,
        preGreetingAudioCommitsRef,
        activeResponseRef,
        responseStallTimerRef,
        pendingResponseRef,
        tokensUsedRef,
        currentUserTranscriptRef,
        currentUserTranscriptItemRef,
        committedUserTranscriptItemsRef,
        userTranscriptFlushTimerRef,
        setPendingCallEnd,
        setPendingReminder,
        setPendingEndAfterSpeaking,
        setIsSpeaking,
        setMicrophoneForBotSpeaking,
        setIsMuted,
        setError,
        setUserPartial,
        addMessage,
        triggerResponse,
        handleFunctionCall,
        endSession: () => endSession(),
        pendingReminderRef,
        pendingCallEndRef,
        publicKey: config.publicKey,
      })
    } catch (handlerError) {
      // Catch any unexpected errors in message handling to prevent crash
      console.error('❌ Error handling data channel message:', handlerError)
      console.log('   Message type was:', message?.type)
    }
  }, [addMessage, setMicrophoneForBotSpeaking, triggerResponse])

  // Phase B: keep isSpeakingRef in sync so the live-transcript gate can read it
  // synchronously (avoid transcribing the bot's own voice bleeding through).
  useEffect(() => {
    isSpeakingRef.current = isSpeaking
  }, [isSpeaking])

  // Phase B: start/stop the Azure Speech streaming live transcript. Voice-only
  // and flag-gated — when config.liveTranscriptEngine === 'azure_speech', the
  // Realtime built-in transcription is disabled (see sendInitialGreeting) and
  // this streams the displayed user transcript instead. Final utterances are
  // fed through the EXISTING data-channel handler as a synthetic
  // input_audio_transcription.completed event, so all goodbye/booking logic is
  // reused unchanged.
  useEffect(() => {
    if (config.liveTranscriptEngine !== 'azure_speech') return
    if (!isSessionActive) return
    let cancelled = false
    void (async () => {
      const handle = await startLiveSpeechTranscript({
        publicKey: config.publicKey,
        language: selectedLanguageRef.current,
        isBotSpeaking: () => isSpeakingRef.current,
        onPartial: (text) => {
          if (!cancelled) setUserPartial(text)
        },
        onFinal: (text) => {
          if (cancelled) return
          setUserPartial('')
          handleDataChannelMessage({
            data: JSON.stringify({
              type: 'conversation.item.input_audio_transcription.completed',
              transcript: text,
            }),
          } as MessageEvent)
        },
        log: (m) => console.log('🗣️ [live-transcript]', m),
      })
      if (cancelled) {
        handle?.stop()
        return
      }
      liveSpeechHandleRef.current = handle
    })()
    return () => {
      cancelled = true
      liveSpeechHandleRef.current?.stop()
      liveSpeechHandleRef.current = null
      setUserPartial('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSessionActive, config.liveTranscriptEngine, config.publicKey])

  // Handle function calls from the bot
  const handleFunctionCall = async (callId: string, name: string, args: Record<string, unknown>) => {
    console.log('🔧 Function call:', name, args, 'call_id:', callId)

    // Start typing sound to fill the latency gap while fetching data
    // Use 'clicking' profile for scheduling-related calls, 'typing' for search
    const soundProfile = name === 'search_knowledge_base' ? 'typing' : 'clicking'
    typingSound.start(soundProfile)

    try {
      let result: unknown
      
      // Get customerId for multi-tenant API calls
      const customerId = sessionDataRef.current?.customerId

      switch (name) {
        case 'get_available_slots':
          // Wave 3b: handler in assistant-tools/run-calendly-slots.
          result = await runGetAvailableSlots({
            args,
            customerId,
            setAvailableSlots,
          })
          break

        case 'check_specific_slot':
          // Wave 3b: handler in assistant-tools/run-calendly-slots.
          result = await runCheckSpecificSlot({
            args,
            customerId,
            configRef,
          })
          break

        case 'book_meeting': {
          // Wave 3b: handler in assistant-tools/run-book-meeting.
          // Helper owns deferred-mode contact-popup gate, name
          // resolution, calendly-book POST, ScheduledMeeting +
          // Calendly embed open, and the in-line function_call_output
          // send (so the model speaks while the embed is open).
          const bookOutcome = await runBookMeeting({
            args,
            transcript,
            customerId,
            callId,
            contactInfo,
            contactInfoRef,
            reachedSchedulingRef,
            pendingCustomerNameRef,
            dataChannelRef,
            scheduledMeetingRef,
            setScheduledMeeting,
            setShowCalendlyEmbed,
            setBookingConfirmed,
            waitForDeferredContactInfo,
            extractCustomerNameFromTranscript,
            triggerResponse,
            selectedLanguageRef,
          })
          if (bookOutcome.kind === 'handled') {
            // Helper already sent function_call_output + triggered response.
            return
          }
          result = bookOutcome.output
          break
        }
        case 'schedule_callback':
          // Wave 3b: full handler lives in
          // assistant-tools/run-schedule-callback. The helper owns
          // contact-popup gating in during_conversation mode,
          // datetime + name resolution, ScheduledMeeting commit,
          // bookingConfirmed flip, and pending-end-after-speaking.
          result = await runScheduleCallback({
            args,
            transcript,
            configRef,
            contactInfoRef,
            pendingCustomerNameRef,
            dataChannelRef,
            selectedLanguageRef,
            scheduledMeetingRef,
            setScheduledMeeting,
            setBookingConfirmed,
            setPendingEndAfterSpeaking,
            waitForDeferredContactInfo,
            formatTime,
            extractCustomerNameFromTranscript,
          })
          break

        case 'verify_resident_id': {
          // Residents (Dona) VOICE-only — Tier-1 ת.ז FORMAT pre-check.
          // Deterministic server checksum (no DB lookup). On valid, remember
          // the digits so we can sanity-check the later confirm step.
          const rawTz = typeof args.resident_tz === 'string' ? args.resident_tz : ''
          const tzDigits = rawTz.replace(/\D/g, '')
          try {
            const resp = await postResidentVerifyId(
              { resident_tz: rawTz, publicKey: config.publicKey },
              config.publicKey,
            )
            const data = (await resp.json().catch(() => ({}))) as { valid?: boolean; reason?: string }
            const valid = data?.valid === true
            residentVerifiedTzRef.current = valid ? tzDigits : null
            result = valid
              ? { valid: true }
              : {
                  valid: false,
                  reason: data?.reason || 'invalid',
                  instruction:
                    'The ת.ז does not sound like a valid Israeli ID. Ask the caller to say it again slowly, digit by digit. Do NOT ask for the apartment number yet, and do NOT call search_knowledge_base.',
                }
          } catch {
            result = {
              valid: false,
              instruction:
                'Could not verify the ת.ז right now. Ask the caller to repeat it slowly, digit by digit.',
            }
          }
          break
        }

        case 'confirm_resident_identity': {
          // Residents (Dona) VOICE-only — explicit caller read-back confirmation
          // gate. The model calls this only AFTER reading the ת.ז + apartment
          // back aloud and the caller said yes. We store the confirmed pair;
          // search_knowledge_base then unlocks for exactly these values.
          const tzDigits = (typeof args.resident_tz === 'string' ? args.resident_tz : '').replace(/\D/g, '')
          const apartment = (typeof args.resident_apartment === 'string' ? args.resident_apartment : '').trim()
          if (!tzDigits || !apartment) {
            result = {
              confirmed: false,
              instruction:
                'Both the ת.ז and the apartment number are required. Ask the caller for whichever is missing, read it back, get a yes, then call confirm_resident_identity again.',
            }
            break
          }
          residentConfirmedRef.current = { tz: tzDigits, apartment }
          result = { confirmed: true }
          break
        }

        case 'search_knowledge_base': {
          // Wave 3b: full handler lives in
          // assistant-tools/run-knowledge-base-search. The helper owns
          // industry-specific query rewriting (travel enrichment,
          // abroad filter, geo rerank + retry; insurance filler
          // stripping), the formatter side effect on pendingLinksRef,
          // and updates lastTravelContextHintsRef.
          const isResidents = sessionDataRef.current?.industry === 'residents'
          const rawTz = typeof args.resident_tz === 'string' ? args.resident_tz : ''
          const rawApt = typeof args.resident_apartment === 'string' ? args.resident_apartment : ''
          const wantsPersonal = isResidents && (rawTz.length > 0 || rawApt.length > 0)
          let residentVerify: { tz: string; apartment: string } | undefined

          if (wantsPersonal) {
            const tzDigits = rawTz.replace(/\D/g, '')
            const apartment = rawApt.trim()
            const confirmed = residentConfirmedRef.current
            const isConfirmed = !!confirmed && confirmed.tz === tzDigits && confirmed.apartment === apartment
            if (!isConfirmed) {
              // Self-correcting tool error: force the read-back + confirm step
              // before any personal-contract retrieval.
              result =
                'NOT_VERIFIED: Before searching this renter\'s personal contract you must confirm their identity. Read the ת.ז back to the caller digit by digit AND read the apartment number back, ask them to confirm both are correct, and once they say yes call confirm_resident_identity with the exact ת.ז and apartment. Only then call search_knowledge_base again with resident_tz and resident_apartment. Do NOT reveal any contract details yet.'
              break
            }
            residentVerify = { tz: tzDigits, apartment }
          }

          result = await runKnowledgeBaseSearch({
            query: args.query,
            publicKey: config.publicKey,
            sessionDataRef,
            transcriptMessagesRef,
            lastUserMessageRef,
            lastTravelContextHintsRef,
            pendingLinksRef,
            selectedLanguageRef,
            ...(residentVerify ? { residentVerify } : {}),
          })
          break
        }
        default:
          result = 'Unknown function'
      }

      // Stop typing sound — data is ready, bot will speak
      typingSound.stop()

      // Send function result back to bot
      if (dataChannelRef.current?.readyState === 'open') {
        dataChannelRef.current.send(JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'function_call_output',
            call_id: callId,
            output: JSON.stringify(result)
          }
        }))
        // Trigger response immediately (official OpenAI pattern - no delay)
        triggerResponse('function-output', true)
      }
    } catch (err) {
      console.error('Function call error:', err)
      // Stop typing sound on error too
      typingSound.stop()
      // RECOVERY: Send error result back to bot so it can respond gracefully
      if (dataChannelRef.current?.readyState === 'open') {
        try {
          dataChannelRef.current.send(JSON.stringify({
            type: 'conversation.item.create',
            item: {
              type: 'function_call_output',
              call_id: callId,
              output: JSON.stringify({ error: 'Function failed. Apologize to the customer and offer to help another way.' })
            }
          }))
          // Trigger response immediately
          triggerResponse('function-error-recovery', true)
        } catch (sendErr) {
          console.error('Failed to send error recovery:', sendErr)
        }
      }
    }
  }

  // Wave 0b follow-up: keep handleFunctionCallRef in sync so the
  // useDebugOverlayEvents callTool dispatcher can invoke it.
  handleFunctionCallRef.current = handleFunctionCall

  // Initialize WebRTC connection
  // activeConfig: The merged config (props + server widgetConfig) to use for this session
  const initializeWebRTC = async (ephemeralKey: string, contextPropertyName: string | null = null, activeConfig: typeof config = config) => {
    const sessionData = sessionDataRef.current
    if (!sessionData) throw new Error('No session data')

    const peerConnection = new RTCPeerConnection()
    peerConnectionRef.current = peerConnection

    // Create audio element for bot's voice
    const audioElement = document.createElement('audio')
    audioElement.autoplay = true
    audioElementRef.current = audioElement
    document.body.appendChild(audioElement)

    // Get microphone access
    // CRITICAL: Disable track IMMEDIATELY before adding to peer connection
    // This ensures NO audio is sent until after greeting completes
    const hardening = activeConfig.voiceHardening
    const audioConstraints = hardening?.enabled
      ? {
          echoCancellation: hardening.settings.echo_cancellation,
          noiseSuppression: hardening.settings.noise_suppression,
          autoGainControl: hardening.settings.auto_gain_control,
          channelCount: 1,
        }
      : true

    const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints })

    if (hardening?.enabled) {
      console.log('🎛️ Voice hardening capture constraints applied:', audioConstraints)
    }
    stream.getAudioTracks().forEach(track => {
      track.enabled = false // Pre-disable to prevent any audio during greeting
      peerConnection.addTrack(track)
    })
    console.log('🎤 Microphone ready (pre-muted for greeting)')

    // Setup audio mixing for full conversation recording (user + bot)
    peerConnection.ontrack = (event) => {
      const remoteStream = event.streams[0]
      audioElement.srcObject = remoteStream
      console.log('🔊 Audio connected')

      // Wave 5e2: mixed-recording setup (with user-only fallback) extracted
      // to startMixedRecording. It owns the AudioContext mixer, the
      // MediaRecorder lifecycle, and the chunk-upload buffering hook.
      startMixedRecording(stream, remoteStream, {
        audioContextRef,
        mixedStreamRef,
        mediaRecorderRef,
        audioChunksRef,
        recordedAudioBlobRef,
        chunkUploadActiveRef,
        chunkBufferRef,
        chunkUploadPromisesRef,
        flushChunkBuffer,
        chunkFlushInterval: CHUNK_FLUSH_INTERVAL,
      })
    }

    // Create data channel
    const dataChannel = peerConnection.createDataChannel('realtime-channel')
    dataChannelRef.current = dataChannel

    dataChannel.onopen = () => {
      // Wave 5e3: session.update + greeting trigger + retry timer extracted
      // to sendInitialGreeting. It owns the full onopen sequence (session
      // config, mic pre-mute, greeting request, retry watchdog).
      //
      // Cross-channel handoff: if shared memory has prior turns (user
      // just switched from chat), pass them through so the Realtime
      // session is seeded with the full conversation context BEFORE the
      // model's first response. The greeting is skipped in that path and
      // the bot continues mid-conversation.
      const priorMessages = crossChannelMemory?.hasMemory()
        ? crossChannelMemory.snapshot().map((m) => ({ role: m.role, content: m.content }))
        : undefined
      sendInitialGreeting({
        dataChannel,
        peerConnection,
        contextPropertyName,
        activeConfig,
        sessionData,
        selectedLanguageRef,
        greetingRetryTimeoutRef,
        responseHadAudioRef,
        activeResponseRef,
        buildSystemInstructions,
        priorMessages,
      })
    }

    dataChannel.onmessage = handleDataChannelMessage
    dataChannel.onclose = () => console.log('📴 Data channel closed')
    dataChannel.onerror = (err) => console.error('❌ Data channel error:', err)

    // Wave 5e1: SDP offer/answer exchange extracted to negotiateRealtimeSdp.
    await negotiateRealtimeSdp(peerConnection, sessionData.endpoint, ephemeralKey)

    setIsSessionActive(true)
    setIsConnecting(false)
    sessionStartTime.current = Date.now()
    transcriptSentRef.current = false
    postCallInProgressRef.current = false
    recordingDataRef.current = { url: null, storagePath: null, size: 0, type: 'audio/webm' }
    // Initialize chunk upload state — activate AFTER WebRTC is connected
    // (MediaRecorder already started above, first ondataavailable fires in ~1s)
    chunkIndexRef.current = 0
    chunkBufferRef.current = []
    chunkUploadPromisesRef.current = []
    chunkUploadActiveRef.current = true
    console.log('📦 Progressive chunk upload enabled (flush every ~10s)')
  }

  // Wave 5e4: chunk-upload body extracted to flushChunkBuffer helper.
  // Thin wrapper preserves the () => Promise<boolean> signature consumed
  // by startMixedRecording and finalizeSessionRecording.
  const flushChunkBuffer = (): Promise<boolean> =>
    flushChunkBufferHelper({
      chunkBufferRef,
      chunkIndexRef,
      sessionDataRef,
      publicKey: configRef.current.publicKey,
    })

  // Start voice session
  // Wave 5f: startSession body extracted to startVoiceSession helper.
  // Owns: state resets, ephemeralKey fetch, error mapping, widgetConfig
  // merge, propertyName context — then hands off to initializeWebRTC.
  const startSession = (): Promise<void> =>
    startVoiceSession({
      config,
      sessionDataRef,
      currentBotResponseRef: currentBotResponse,
      goodbyeSentRef,
      initialGreetingCompleteRef,
      preGreetingAudioCommitsRef,
      transcriptionsToSkipRef,
      pendingGreetingUnmuteRef,
      setError,
      setIsConnecting,
      setTranscript,
      setScheduledMeeting,
      setShowCalendlyEmbed,
      setBookingConfirmed,
      setPendingCallEnd,
      setPendingEndAfterSpeaking,
      setIsMuted,
      setConfig,
      setPropertyName,
      initializeWebRTC,
    })

  // End voice session
  const endSession = async () => {
    // ========================================
    // PHASE 1: IMMEDIATE UI UPDATE (Client-side)
    // ========================================
    // Close all connections and disable UI immediately
    // User sees instant feedback but CANNOT close page during upload

    console.log('🛑 Starting session end - closing connections...')
    
    // Mark session as completed to prevent useEffects from re-showing pre-session screens
    sessionCompletedRef.current = true
    // Keep postCallInProgressRef true so beacon fires if tab closes during finalization
    postCallInProgressRef.current = true
    // Stop any playing typing sound
    typingSound.stop()
    // Phase B: stop the Azure Speech streaming recognizer if running.
    liveSpeechHandleRef.current?.stop()
    liveSpeechHandleRef.current = null
    setUserPartial('')
    
    // Show uploading overlay to prevent closing during upload
    setIsUploading(true)

    // Wave 5b: pending-timer cancellation extracted to clearVoiceSessionTimers.
    clearVoiceSessionTimers({
      greetingRetryTimeoutRef,
      responseStallTimerRef,
      idleTimeoutRef,
    })

    // ========================================
    // CRITICAL: Stop MediaRecorder FIRST, BEFORE closing AudioContext
    // The MediaRecorder records from AudioContext's destination stream.
    // Closing AudioContext first kills the stream → MediaRecorder can't
    // finalize properly → recordedAudioBlobRef stays null → no recording.
    // ========================================
    // Wave 5a: connection teardown extracted to closeRealtimeConnection.
    // Order is load-bearing: stops MediaRecorder + waits for blob before
    // closing AudioContext, then drops dataChannel, peerConnection, and
    // the audio element. UI state writes follow inline so render order
    // is preserved.
    await closeRealtimeConnection({
      mediaRecorderRef,
      recordedAudioBlobRef,
      audioContextRef,
      dataChannelRef,
      peerConnectionRef,
      audioElementRef,
    })

    // IMMEDIATELY update UI state - disable button and reset UI
    setIsSessionActive(false)
    setIsSpeaking(false)
    setIsMuted(false)
    currentBotResponse.current = ''
    console.log('✅ Session ended - UI updated immediately')
    console.log('   User can now close the page - processing continues server-side')

    // ========================================
    // PHASE 2: PREPARE DATA & SEND TO SERVER (Background)
    // ========================================
    // Heavy processing happens server-side
    // User doesn't need to wait for this

    // Calculate duration
    const duration = Math.round((Date.now() - sessionStartTime.current) / 1000)

    // Determine outcome - use refs to avoid stale closures
    // 'scheduled' = meeting confirmed in Calendly (user clicked confirm button)
    // 'question_answered' = user had conversation but no meeting
    let outcome: ConversationOutcome = 'unknown'
    const hasConfirmedMeeting = !!scheduledMeetingRef.current && bookingConfirmedRef.current
    if (hasConfirmedMeeting) {
      outcome = 'scheduled'
    } else if (transcript.length > 2) {
      outcome = 'question_answered'
    }
    console.log('📊 Session outcome:', { outcome, hasConfirmedMeeting, hasMeetingData: !!scheduledMeetingRef.current, bookingConfirmed: bookingConfirmedRef.current })

    // Recording blob was already captured in Phase 1 (promise-based wait)
    // No need for a fixed timeout — the blob is guaranteed ready if MediaRecorder was active

    // ========================================
    // SHORT CALL CHECK: Calls < 30 seconds skip audio upload + n8n
    // but still proceed to Phase 4 so usage is logged for billing
    // ========================================
    const MIN_USEFUL_DURATION_SECONDS = 30
    const isShortCall = duration < MIN_USEFUL_DURATION_SECONDS

    if (isShortCall) {
      console.log(`⏱️ Short call (${duration}s < ${MIN_USEFUL_DURATION_SECONDS}s) — skipping audio upload, n8n will be skipped server-side`)
    }

    // ========================================
    // PHASE 2.5: POST-SESSION LEAVE DETAILS POPUP (deferred mode only)
    // If contact form was deferred and user never provided details,
    // show "רוצה שנחזור אליך?" popup before sending transcript
    // ========================================
    const isDeferredMode = (configRef.current.contactFormTiming || 'none') === 'before_scheduling'
    const isDuringConvPostCall = (configRef.current.contactFormTiming || 'none') === 'during_conversation'
    
    if (!contactInfoRef.current && (isDeferredMode || isDuringConvPostCall) && !isShortCall) {
      console.log('📋 Deferred mode: showing leave-details popup...')
      setIsUploading(false) // Hide uploading overlay so popup is visible
      await new Promise<void>((resolve) => {
        // Store resolver — will be called by popup submit/dismiss callbacks
        leaveDetailsResolveRef.current = resolve
        setShowLeaveDetailsPopup(true)
        // Safety timeout: auto-resolve after 30s
        setTimeout(() => {
          leaveDetailsResolveRef.current = null
          setShowLeaveDetailsPopup(false)
          resolve()
        }, 30000)
      })
      setIsUploading(true) // Re-show uploading overlay for remaining processing
    }
    
    const isAnonymous = !contactInfoRef.current

    // ========================================
    // PHASE 3: FINALIZE RECORDING (CHUNKED OR FALLBACK)
    // (Skipped for short calls — no useful audio to store)
    // ========================================
    // Progressive chunk upload runs DURING the call (every ~10s).
    // At call end, we finalize: flush remaining buffer, wait for in-flight
    // uploads, then call /api/finalize-recording to merge chunks into one file.
    // If chunked upload wasn't active, fall back to original single-blob upload.

    let audioUrl: string | null = null
    let audioStoragePath: string | null = null

    // Skip only for short calls — anonymous calls should still retain recordings
    if (!isShortCall && recordedAudioBlobRef.current) {
      // Wave 5c: chunked-finalize + single-blob fallback extracted to
      // finalizeSessionRecording. It mutates recordingDataRef on success
      // and returns the resolved URLs so we can attach them to /api/transcript.
      const result = await finalizeSessionRecording({
        recordedAudioBlobRef,
        sessionDataRef,
        chunkUploadActiveRef,
        chunkIndexRef,
        chunkBufferRef,
        chunkUploadPromisesRef,
        recordingDataRef,
        flushChunkBuffer,
        publicKey: config.publicKey,
        duration,
      })
      audioUrl = result.audioUrl
      audioStoragePath = result.audioStoragePath
    }

    // ========================================
    // PHASE 4: SEND TO API/TRANSCRIPT
    // ========================================
    // api/transcript handles:
    // 1. Usage logging to Supabase
    // 2. Forwarding to n8n webhook (server-to-server, no CORS)
    // 3. Sends audio URL from Supabase Storage (no more base64)

    // Wave 5d: Phase 4 (Calendly fetch await + payload assembly + transcript POST)
    // extracted to sendSessionTranscript. It owns its own try/catch and sets
    // transcriptSentRef on success.
    await sendSessionTranscript({
      sessionDataRef,
      scheduledMeetingRef,
      bookingConfirmedRef,
      contactInfoRef,
      contactInfoSourceRef,
      reachedSchedulingRef,
      recordedAudioBlobRef,
      tokensUsedRef,
      calendlyEventFetchRef,
      transcriptSentRef,
      configRef,
      selectedLanguageRef,
      transcript,
      duration,
      outcome,
      audioUrl,
      audioStoragePath,
      isAnonymous,
      extractCustomerNameFromTranscript,
    })

    // Hide uploading overlay - upload complete
    setIsUploading(false)
    postCallInProgressRef.current = false
    console.log('🎉 Session end complete')
  }

  // Wave 6 cross-channel safety net: mirror the latest endSession closure
  // into a ref so the unmount cleanup useEffect (declared near the top of
  // the component) can call it without putting it in its dep array.
  endSessionRef.current = endSession

  // Toggle mute
  const toggleMute = () => {
    if (!peerConnectionRef.current) return

    peerConnectionRef.current.getSenders().forEach(sender => {
      if (sender.track?.kind === 'audio') {
        sender.track.enabled = !sender.track.enabled
        const muted = !sender.track.enabled
        // Wave 6e1: setIsMuted now writes isMutedRef synchronously, so
        // the previously-paired `isMutedRef.current = muted` is gone.
        setIsMuted(muted)
      }
    })
  }

  // Toggle interruption blocking mode
  const toggleInterruption = () => {
    const newValue = !blockInterruption
    // Wave 6e4: setter writes blockInterruptionRef synchronously.
    setBlockInterruption(newValue)

    // If toggling while bot is speaking, immediately apply the mic state
    // BUT respect manual mute: if user muted, keep it muted regardless
    if (isSpeaking && peerConnectionRef.current) {
      peerConnectionRef.current.getSenders().forEach(sender => {
        if (sender.track?.kind === 'audio') {
          // Only change mic state if NOT manually muted
          if (!isMuted) {
            // If blocking interruption AND bot is speaking, mute; otherwise unmute
            sender.track.enabled = !newValue
            console.log(`🎤 Toggled: Mic ${newValue ? 'muted (no-interrupt ON)' : 'unmuted (interrupt allowed)'}`)
          } else {
            console.log(`🎤 User has manually muted - keeping muted regardless of interrupt mode`)
          }
        }
      })
    }

    // Note: For Azure GA Protocol, turn_detection is set at session creation (API side)
    // and cannot be updated mid-session via client-side session.update.
    // The block interruption feature works via microphone muting (already implemented above)
    console.log(`🔄 Interruption ${newValue ? 'blocked (mic muted during bot speech)' : 'allowed (user can interrupt)'}`)
  }

  // Clear transcript
  const clearTranscript = () => {
    setTranscript([])
    currentBotResponse.current = ''
  }

  if (!isOpen) return null

  return (
    <>
      <AnimatePresence>
        {/* Wave 7b: extracted to Backdrop */}
        <Backdrop
          key="backdrop"
          onClick={() => !isSessionActive && !isUploading && handlePanelClose()}
        />

        {/* Contact Form Overlay (full form — normal mode only) */}
        {showContactForm && (
          <ContactForm
            key="contact-form"
            primaryColor={config.primaryColor}
            onSubmit={handleContactFormSubmit}
            onClose={() => {
              setShowContactForm(false)
              handlePanelClose()
            }}
          />
        )}

        {/* Scheduling Contact Popup (deferred mode — name+email+phone before Calendly) */}
        {showSchedulingPopup && (
          <SchedulingContactPopup
            key="scheduling-popup"
            primaryColor={config.primaryColor}
            customerName={pendingCustomerNameRef.current || undefined}
            defaultName={pendingCustomerNameRef.current || undefined}
            mode={(configRef.current.contactFormTiming || 'none') === 'during_conversation' ? 'callback' : 'calendly'}
            onSubmit={(email, phone, formName) => {
              const resolvedName = formName || pendingCustomerNameRef.current || undefined
              const info = { email, phone, name: resolvedName }
              contactInfoRef.current = info
              contactInfoSourceRef.current = (configRef.current.contactFormTiming || 'none') === 'during_conversation'
                ? 'during_conversation_popup'
                : 'pre_calendly_form'
              setContactInfo(info)
              setShowSchedulingPopup(false)
              console.log('✅ Scheduling popup contact info collected:', info)
              // Facebook Pixel: the visitor submitted contact details to receive
              // the scheduling link — this is the campaign's Lead conversion.
              // No-op unless the customer enabled a pixel. Voice channel.
              trackFacebookLead()
              // Resolve the book_meeting promise
              if (deferredContactResolveRef.current) {
                deferredContactResolveRef.current(info)
                deferredContactResolveRef.current = null
              }
            }}
            onDismiss={() => {
              setShowSchedulingPopup(false)
              console.log('ℹ️ User dismissed scheduling popup')
              pendingCustomerNameRef.current = null
              // Cancel the pending promise — book_meeting must not proceed without contact info
              if (deferredContactResolveRef.current) {
                deferredContactResolveRef.current(null)
                deferredContactResolveRef.current = null
              }
            }}
          />
        )}

        {/* Pre-Session Screen (deferred mode — language + tips + consent, no email/phone) */}
        {showPreSessionScreen && (
          <PreSessionScreen
            key="pre-session-screen"
            primaryColor={config.primaryColor}
            onReady={handlePreSessionReady}
            onClose={() => {
              setShowPreSessionScreen(false)
              handlePanelClose()
            }}
          />
        )}

        {/* Leave Details Popup (post-session — deferred mode, no contact info collected) */}
        {showLeaveDetailsPopup && (
          <LeaveDetailsPopup
            key="leave-details-popup"
            primaryColor={config.primaryColor}
            onSubmit={(email, phone) => {
              const info = { email, phone }
              contactInfoRef.current = info
              contactInfoSourceRef.current = 'post_session_popup'
              setContactInfo(info)
              setShowLeaveDetailsPopup(false)
              console.log('✅ Post-session contact info collected:', info)
              // Resolve the endSession promise
              if (leaveDetailsResolveRef.current) {
                leaveDetailsResolveRef.current()
                leaveDetailsResolveRef.current = null
              }
            }}
            onDismiss={() => {
              setShowLeaveDetailsPopup(false)
              console.log('ℹ️ User dismissed leave-details popup')
              // Resolve the endSession promise (proceed without contact info)
              if (leaveDetailsResolveRef.current) {
                leaveDetailsResolveRef.current()
                leaveDetailsResolveRef.current = null
              }
            }}
          />
        )}

        {/* Wave 7a: extracted to UploadingOverlay */}
        {isUploading && <UploadingOverlay key="uploading-overlay" primaryColor={config.primaryColor} />}

        {/* Wave 7c: extracted to CalendlyPanel (desktop side-by-side + mobile overlay) */}
        <CalendlyPanel
          key="calendly-panel"
          isMobile={isMobile}
          showCalendlyEmbed={showCalendlyEmbed}
          showCalendlyOverlay={showCalendlyOverlay}
          scheduledMeeting={scheduledMeeting}
          primaryColor={config.primaryColor}
          panelWidth={config.panelWidth}
          onClose={() => setShowCalendlyEmbed(false)}
        />

        {/* Voice Bot Panel */}
        <motion.div
          key="panel"
          initial={{ opacity: 0, x: config.panelWidth }}
          animate={{
            opacity: isMobile && showCalendlyOverlay ? 0.3 : 1,
            x: 0
          }}
          exit={{ opacity: 0, x: config.panelWidth }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            height: '100dvh',  // dvh = dynamic viewport height (excludes iOS URL bar)
            width: config.panelWidth,
            maxWidth: '100vw',
            backgroundColor: 'white',
            boxShadow: '-4px 0 30px rgba(0, 0, 0, 0.15)',
            zIndex: 10000,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            direction: 'rtl',
          }}
        >
          {/* Wave 7d: extracted to VoiceBotHeader */}
          <VoiceBotHeader
            botName={config.botName}
            companyName={config.companyName}
            primaryColor={config.primaryColor}
            isUploading={isUploading}
            onClose={onClose}
            onSwitchToChat={onSwitchToChat}
            switchToChatLabel={switchToChatLabel}
          />

          {/* Wave 7e: extracted to SessionControls */}
          <SessionControls
            isSessionActive={isSessionActive}
            isConnecting={isConnecting}
            isMuted={isMuted}
            blockInterruption={blockInterruption}
            showInterruptTooltip={showInterruptTooltip}
            contactFormTiming={config.contactFormTiming}
            hasContactInfo={!!contactInfo}
            primaryColor={config.primaryColor}
            botName={config.botName}
            onStartSession={startSession}
            onEndSession={endSession}
            onToggleMute={toggleMute}
            onToggleInterruption={toggleInterruption}
            onShowInterruptTooltip={setShowInterruptTooltip}
          />

          {/* Error Display */}
          <ErrorBanner error={error} />

          {/* Speaking Indicator (Wave 7f) */}
          <SpeakingIndicator
            isSpeaking={isSpeaking}
            botName={config.botName}
            primaryColor={config.primaryColor}
          />

          {/* Booking Status (Wave 7k) */}
          <BookingStatusBanner
            scheduledMeeting={scheduledMeeting}
            bookingConfirmed={bookingConfirmed}
            calendlyOwnerName={config.calendlyOwnerName}
          />

          {/* Transcript (Wave 7i) */}
          <Transcript
            transcript={transcript}
            transcriptRef={transcriptRef}
            onClear={clearTranscript}
            primaryColor={config.primaryColor}
            publicKey={config.publicKey}
            poweredByText={config.poweredByText}
            botName={config.botName}
            livePartial={userPartial}
          />

          {/* Footer */}
        </motion.div>
      </AnimatePresence>
    </>
  )
}

export default VoiceBotPanel
